/**
 * Gym Shorts v4 — Pack template behavior (v3-bundle UX rewrite).
 *
 * Custom elements:
 *   <gs-v4-pack-selector>   — 5-tile pack picker; dispatches gs-v4:pack-change
 *   <gs-v4-design-picker>   — grid container; owns counter + prompt + selection state
 *   <gs-v4-design-card>     — single design card (expandable, selectable)
 *   <gs-v4-tee-picker>      — tee grid container; exposes getSelectedTees()
 *   <gs-v4-tee-card>        — single tee card (expandable, selectable)
 *   <gs-v4-coordinator>     — intercepts [data-to-cart] click, batches add via publishEvent
 *
 * +N MORE expand handler: global click on [data-gs-v4-toggle-expand] toggles
 *   sibling [data-gs-v4-overflow] visibility. Mirrors v3 choose-designs pattern.
 *
 * Cart contract (matches gym-shorts-v3-addon.js):
 *   publishEvent('cart:add', { items }, sectionId) → cart-provider._add → /cart/add.js
 *   cart:update fires on completion → release busy lock
 */
(async () => {
  const { publishEvent } = await importModule('Utils');

  // ===========================================================================
  // +N MORE tile expand handler (works for both design picker + tee picker)
  // ===========================================================================
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-gs-v4-toggle-expand]');
    if (!btn) return;
    e.preventDefault();
    // Scope to the closest tab panel first (tee picker may have multiple panels),
    // then fall back to the picker root (design picker has no panels).
    const container =
      btn.closest('[data-gs-v4-panel]') ||
      btn.closest('gs-v4-design-picker, gs-v4-tee-picker');
    if (!container) return;
    const overflow = container.querySelector('[data-gs-v4-overflow]');
    if (!overflow) return;
    const isHidden = overflow.classList.contains('gs-v3-grid--hidden');
    overflow.classList.toggle('gs-v3-grid--hidden');
    const textEl = btn.querySelector('.gs-v3-more-tile__text');
    if (textEl) {
      if (isHidden) {
        if (!btn._originalText) btn._originalText = textEl.innerHTML;
        textEl.innerHTML = 'SHOW LESS';
      } else if (btn._originalText) {
        textEl.innerHTML = btn._originalText;
      }
    }
  });

  // ===========================================================================
  // <gs-v4-pack-selector>
  // ===========================================================================
  class GsV4PackSelector extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this._onClick);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }
    _onClick = (e) => {
      const tile = e.target.closest('[data-pack]');
      if (!tile) return;
      const pack = Number(tile.dataset.pack);
      if (pack === this.selected) return;
      this.select(pack);
    };
    get selected() { return Number(this.dataset.selectedPack); }
    select(pack) {
      this.dataset.selectedPack = String(pack);
      this.querySelectorAll('[data-pack]').forEach(btn => {
        const active = Number(btn.dataset.pack) === pack;
        btn.classList.toggle('is-selected', active);
        btn.setAttribute('aria-checked', String(active));
      });
      document.dispatchEvent(new CustomEvent('gs-v4:pack-change', { detail: { pack } }));
    }
  }
  if (!customElements.get('gs-v4-pack-selector')) {
    customElements.define('gs-v4-pack-selector', GsV4PackSelector);
  }

  // ===========================================================================
  // <gs-v4-size-bar> — global size selector (applies to all selected design cards)
  // ===========================================================================
  class GsV4SizeBar extends HTMLElement {
    connectedCallback() {
      // Use 'change' on radios (native) + 'click' on labels for visual feedback
      this.addEventListener('change', this._onChange);
      // Initial pick: prefer L (or already-checked radio), then first available
      const checked = this.querySelector('[data-gs-v4-global-size]:checked');
      const preferred = checked || this._findRadio('L') || this.querySelector('[data-gs-v4-global-size]:not([disabled])');
      if (preferred) this._select(preferred, { propagate: false });
    }
    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
    }
    _onChange = (e) => {
      const radio = e.target.closest('[data-gs-v4-global-size]');
      if (!radio || radio.disabled) return;
      this._select(radio, { propagate: true });
    };
    _findRadio(label) {
      if (!label) return null;
      const target = String(label).trim().toLowerCase();
      return [...this.querySelectorAll('[data-gs-v4-global-size]:not([disabled])')]
        .find(p => String(p.dataset.sizeLabel).trim().toLowerCase() === target);
    }
    _select(radio, { propagate } = { propagate: true }) {
      const label = radio.dataset.sizeLabel;
      this.dataset.selectedLabel = label;
      radio.checked = true;
      // Update inline "Size: L" label
      const currentLabelEl = this.querySelector('[data-gs-v4-current-label]');
      if (currentLabelEl) currentLabelEl.textContent = label;
      if (propagate) {
        document.querySelectorAll('gs-v4-design-card, gs-v4-tee-card').forEach(card => {
          card.applyGlobalSize?.(label);
        });
      }
    }
  }
  if (!customElements.get('gs-v4-size-bar')) {
    customElements.define('gs-v4-size-bar', GsV4SizeBar);
  }

  // ===========================================================================
  // <gs-v4-design-card> — single card behavior
  // ===========================================================================
  class GsV4DesignCard extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this._onClick);
      // Locked card: pre-pick global default size (size bar's selectedLabel, then L, then first available)
      if (this.dataset.locked === 'true' && this.dataset.selected !== 'true') {
        const sizeBar = document.querySelector('[data-gs-v4-size-bar]');
        const globalLabel = sizeBar?.dataset.selectedLabel;
        const preferred =
          (globalLabel && this._findSizeButton(globalLabel)) ||
          this._findSizeButton('L') ||
          this._findFirstAvailableSize();
        if (preferred) this._select(preferred);
      }
    }
    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }
    _findSizeButton(label) {
      if (!label) return null;
      const target = String(label).trim().toLowerCase();
      return [...this.querySelectorAll('[data-gs-v4-card-size]:not([disabled])')]
        .find(b => String(b.dataset.sizeLabel).trim().toLowerCase() === target);
    }
    _findFirstAvailableSize() {
      return this.querySelector('[data-gs-v4-card-size]:not([disabled])');
    }
    _onClick = (e) => {
      const sizeBtn = e.target.closest('[data-gs-v4-card-size]');
      const sizeTrigger = e.target.closest('[data-gs-v4-size-trigger]');
      const qtyInc = e.target.closest('[data-gs-v4-qty-inc]');
      const qtyDec = e.target.closest('[data-gs-v4-qty-dec]');
      const toggle = e.target.closest('[data-gs-v4-card-toggle]');
      if (qtyInc) { this._changeQty(1); return; }
      if (qtyDec) { this._changeQty(-1); return; }
      if (sizeBtn) { this._pickSize(sizeBtn); return; }
      if (sizeTrigger) { this._toggleSizeExpand(); return; }
      if (toggle) this._onThumbClick();
    };
    get qty() { return Math.max(1, Number(this.dataset.qty || 1)); }
    _changeQty(delta) {
      if (!this.isSelected) return; // qty only meaningful when selected
      const picker = this.closest('gs-v4-design-picker');
      const next = this.qty + delta;
      if (next < 1) return;
      if (delta > 0 && picker && !picker.canAddQty(this, delta)) return;
      this.dataset.qty = String(next);
      this._updateQtyUI();
      this.dispatchSelectionChange();
    }
    _updateQtyUI() {
      const valueEl = this.querySelector('[data-gs-v4-qty-value]');
      if (valueEl) valueEl.textContent = String(this.qty);
      const dec = this.querySelector('[data-gs-v4-qty-dec]');
      const inc = this.querySelector('[data-gs-v4-qty-inc]');
      if (dec) dec.disabled = this.qty <= 1;
      if (inc) {
        const picker = this.closest('gs-v4-design-picker');
        // Guard: picker class might not be upgraded yet when locked card runs on connect
        inc.disabled = typeof picker?.canAddQty === 'function' ? !picker.canAddQty(this, 1) : false;
      }
    }
    get isSelected() { return this.dataset.selected === 'true'; }
    get isLocked() { return this.dataset.locked === 'true'; }
    _onThumbClick() {
      if (this.isLocked) {
        // Locked card thumbnail click = open/close own size pills
        this._toggleSizeExpand();
        return;
      }
      if (this.isSelected) {
        this._deselect();
        return;
      }
      // Apply global size from size bar; if not available, expand pills for manual pick.
      // Honor the picker's pack-budget cap — if requestSelection rejects (e.g. Pack 1
      // already filled by the locked card), abort instead of selecting anyway.
      const sizeBar = document.querySelector('[data-gs-v4-size-bar]');
      const globalLabel = sizeBar?.dataset.selectedLabel;
      const match = globalLabel ? this._findSizeButton(globalLabel) : null;
      const picker = this.closest('gs-v4-design-picker');
      if (match) {
        if (picker && !picker.requestSelection(this)) return;
        this._select(match);
      } else {
        if (picker && !picker.requestSelection(this)) return;
        this._toggleSizeExpand(true);
      }
    }
    _toggleSizeExpand(forceOpen) {
      const next = typeof forceOpen === 'boolean' ? forceOpen : !this.classList.contains('is-expanded-sizes');
      this.classList.toggle('is-expanded-sizes', next);
      this.dataset.expanded = String(next);
      const trigger = this.querySelector('[data-gs-v4-size-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', String(next));
    }
    /** Called by <gs-v4-size-bar> when global size changes — applies to this card if it's currently selected (or locked). */
    applyGlobalSize(label) {
      if (!label) return;
      if (!this.isLocked && !this.isSelected) return;
      const match = this._findSizeButton(label);
      if (match) this._select(match);
    }
    _pickSize(btn) {
      if (btn.disabled) return;
      const sameVariant = this.dataset.variantId === btn.dataset.variantId;
      if (sameVariant && this.isSelected) {
        this._deselect();
        return;
      }
      const picker = this.closest('gs-v4-design-picker');
      if (picker && !this.isSelected && !this.isLocked) {
        const canAdd = picker.requestSelection(this);
        if (!canAdd) return;
      }
      this._select(btn);
    }
    _select(btn) {
      const wasSelected = this.isSelected;
      this.dataset.variantId = btn.dataset.variantId;
      this.dataset.selected = 'true';
      if (!wasSelected) this.dataset.qty = '1';
      this.querySelectorAll('[data-gs-v4-card-size]').forEach(b => {
        const active = b === btn;
        b.classList.toggle('is-selected', active);
        b.setAttribute('aria-pressed', String(active));
      });
      this.classList.add('is-selected');
      const sizeLabelEl = this.querySelector('[data-card-size-label]');
      if (sizeLabelEl) sizeLabelEl.textContent = btn.dataset.sizeLabel || '—';
      this.classList.remove('is-expanded-sizes');
      this.dataset.expanded = 'false';
      const toggleBtn = this.querySelector('[data-gs-v4-card-toggle]');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
      this._updateQtyUI();
      this.dispatchSelectionChange();
    }
    _deselect() {
      this.dataset.variantId = '';
      this.dataset.selected = 'false';
      this.dataset.qty = '1';
      this.querySelectorAll('[data-gs-v4-card-size]').forEach(b => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      this.classList.remove('is-selected');
      this.classList.remove('is-expanded-sizes');
      const sizeLabelEl = this.querySelector('[data-card-size-label]');
      if (sizeLabelEl) sizeLabelEl.textContent = '—';
      this._updateQtyUI();
      this.dispatchSelectionChange();
    }
    dispatchSelectionChange() {
      document.dispatchEvent(new CustomEvent('gs-v4:design-change', {
        bubbles: true,
        detail: { productId: this.dataset.productId, variantId: this.dataset.variantId, selected: this.isSelected, locked: this.isLocked },
      }));
    }
  }
  if (!customElements.get('gs-v4-design-card')) {
    customElements.define('gs-v4-design-card', GsV4DesignCard);
  }

  // ===========================================================================
  // <gs-v4-design-picker> — counter, prompt, selection cap, exposes getSelected()
  // ===========================================================================
  class GsV4DesignPicker extends HTMLElement {
    connectedCallback() {
      this.promptEl = this.querySelector('[data-gs-v4-prompt]');
      this.counterEl = this.querySelector('[data-gs-v4-counter]');

      this._onPackChange = (e) => this._applyPack(e.detail.pack);
      this._onDesignChange = () => this._updateUI();
      document.addEventListener('gs-v4:pack-change', this._onPackChange);
      document.addEventListener('gs-v4:design-change', this._onDesignChange);

      // Initial state — read pack from existing selector
      const sel = document.querySelector('[data-gs-v4-pack-selector]');
      const initialPack = sel ? Number(sel.dataset.selectedPack) : 1;
      this._applyPack(initialPack);
    }
    disconnectedCallback() {
      document.removeEventListener('gs-v4:pack-change', this._onPackChange);
      document.removeEventListener('gs-v4:design-change', this._onDesignChange);
    }
    _applyPack(pack) {
      this.pack = pack;
      this.dataset.pack = String(pack);
      // If new pack is smaller than current selection, prune non-locked cards
      this._enforceBudget();
      this._updateUI();
    }
    /**
     * Called by a card BEFORE selecting itself when not already selected.
     * Returns true if selection allowed; false if cap reached.
     */
    requestSelection(card) {
      // Pack-budget check: sum of qtys already in selection
      if (this._selectedCount() + 1 > this.pack) {
        // FIFO bump: free a slot from a NON-locked card (locked card is always part of pack)
        const others = this._allSelectedCards()
          .filter(c => c !== card && c.dataset.locked !== 'true');
        if (others.length === 0) return false;
        const oldest = others[0];
        if (oldest.qty > 1) {
          oldest.dataset.qty = String(oldest.qty - 1);
          oldest._updateQtyUI();
        } else {
          oldest._deselect();
        }
      }
      return true;
    }
    /** Bump non-locked cards down (FIFO) until selection fits within current pack budget. */
    _enforceBudget() {
      let safety = 50;
      while (this._selectedCount() > this.pack && safety-- > 0) {
        const candidates = this._allSelectedCards()
          .filter(c => c.dataset.locked !== 'true');
        if (candidates.length === 0) break;
        // Reduce qty of the most recently selected non-locked first to keep earlier picks
        const target = candidates[candidates.length - 1];
        if (target.qty > 1) {
          target.dataset.qty = String(target.qty - 1);
          target._updateQtyUI();
        } else {
          target._deselect();
        }
      }
    }
    /** True if `card` can have its qty increased by `delta` without exceeding pack budget. */
    canAddQty(card, delta) {
      const wouldBe = this._selectedCount() + delta;
      return wouldBe <= this.pack;
    }
    _nonLockedSelectedCards() {
      return [...this.querySelectorAll('gs-v4-design-card[data-locked="false"][data-selected="true"]')];
    }
    _allSelectedCards() {
      return [...this.querySelectorAll('gs-v4-design-card[data-selected="true"]')];
    }
    _lockedCard() {
      return this.querySelector('gs-v4-design-card[data-locked="true"]');
    }
    _selectedCount() {
      // Sum of qtys across all selected cards (locked + non-locked)
      return this._allSelectedCards().reduce((sum, c) => sum + c.qty, 0);
    }
    _updateUI() {
      const required = this.pack;
      const selected = this._selectedCount();
      const more = Math.max(0, required - selected);

      // Refresh qty stepper buttons on every card (enabled state depends on budget)
      this.querySelectorAll('gs-v4-design-card').forEach(c => c._updateQtyUI?.());

      // Counter text
      if (this.counterEl) {
        this.counterEl.textContent = `${selected} of ${required} design${required > 1 ? 's' : ''}`;
        this.counterEl.classList.toggle('is-complete', selected >= required);
        this.counterEl.classList.toggle('is-incomplete', selected < required);
      }

      // Prompt text — avoid "Pack N" naming, use natural language instead.
      if (this.promptEl) {
        if (required === 1) {
          this.promptEl.textContent = 'Pick your size below';
        } else if (more > 0) {
          this.promptEl.textContent = `Pick ${more} more design${more > 1 ? 's' : ''} to complete your pack`;
        } else {
          this.promptEl.textContent = 'Your pack is ready — selections below';
        }
      }
    }
    /** Used by coordinator at add-to-cart. Each entry includes qty (default 1). */
    getSelectedDesigns() {
      const out = [];
      const locked = this._lockedCard();
      if (locked && locked.dataset.selected === 'true' && locked.dataset.variantId) {
        out.push({ productId: locked.dataset.productId, variantId: locked.dataset.variantId, qty: locked.qty, locked: true });
      }
      this._nonLockedSelectedCards().forEach(c => {
        out.push({ productId: c.dataset.productId, variantId: c.dataset.variantId, qty: c.qty, locked: false });
      });
      return out;
    }
    /** True if selections are sufficient for the current pack. */
    isComplete() {
      return this._selectedCount() >= this.pack;
    }
    /** Highlight the first not-yet-completed card. */
    flagIncomplete() {
      const locked = this._lockedCard();
      if (locked && locked.dataset.selected !== 'true') {
        locked.classList.add('has-error');
        locked.scrollIntoView({ behavior: 'smooth', block: 'center' });
        locked.dataset.expanded = 'true';
        const tBtn = locked.querySelector('[data-gs-v4-card-toggle]');
        if (tBtn) tBtn.setAttribute('aria-expanded', 'true');
        setTimeout(() => locked.classList.remove('has-error'), 3000);
        return;
      }
      // Otherwise flag the picker itself so user sees they need more designs
      this.classList.add('has-error');
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => this.classList.remove('has-error'), 3000);
    }
  }
  if (!customElements.get('gs-v4-design-picker')) {
    customElements.define('gs-v4-design-picker', GsV4DesignPicker);
  }

  // ===========================================================================
  // <gs-v4-tee-card> — single tee, same pattern as design-card but optional
  // ===========================================================================
  class GsV4TeeCard extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this._onClick);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }
    _onClick = (e) => {
      const sizeBtn = e.target.closest('[data-gs-v4-tee-size]');
      const trigger = e.target.closest('[data-gs-v4-tee-size-trigger]');
      const qtyInc = e.target.closest('[data-gs-v4-tee-qty-inc]');
      const qtyDec = e.target.closest('[data-gs-v4-tee-qty-dec]');
      const toggle = e.target.closest('[data-gs-v4-tee-toggle]');
      if (qtyInc) { this._changeQty(1); return; }
      if (qtyDec) { this._changeQty(-1); return; }
      if (sizeBtn) { this._pickSize(sizeBtn); return; }
      if (trigger) { this._toggleSizeExpand(); return; }
      if (toggle) this._onThumbClick();
    };
    get isSelected() { return this.dataset.selected === 'true'; }
    get qty() { return Math.max(1, Number(this.dataset.qty || 1)); }
    _changeQty(delta) {
      if (!this.isSelected) return;
      const next = this.qty + delta;
      if (next < 1) return;
      this.dataset.qty = String(next);
      this._updateQtyUI();
    }
    _updateQtyUI() {
      const valueEl = this.querySelector('[data-gs-v4-tee-qty-value]');
      if (valueEl) valueEl.textContent = String(this.qty);
      const dec = this.querySelector('[data-gs-v4-tee-qty-dec]');
      if (dec) dec.disabled = this.qty <= 1;
    }
    _findSizeButton(label) {
      if (!label) return null;
      const target = String(label).trim().toLowerCase();
      return [...this.querySelectorAll('[data-gs-v4-tee-size]:not([disabled])')]
        .find(b => String(b.dataset.sizeLabel).trim().toLowerCase() === target);
    }
    _onThumbClick() {
      if (this.isSelected) { this._deselect(); return; }
      const sizeBar = document.querySelector('[data-gs-v4-size-bar]');
      const globalLabel = sizeBar?.dataset.selectedLabel;
      const match = globalLabel ? this._findSizeButton(globalLabel) : null;
      if (match) this._select(match);
      else this._toggleSizeExpand(true);
    }
    _toggleSizeExpand(forceOpen) {
      const next = typeof forceOpen === 'boolean' ? forceOpen : !this.classList.contains('is-expanded-sizes');
      this.classList.toggle('is-expanded-sizes', next);
      this.dataset.expanded = String(next);
      const trigger = this.querySelector('[data-gs-v4-tee-size-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', String(next));
    }
    /** Called by <gs-v4-size-bar> when global size changes — re-apply to selected cards. */
    applyGlobalSize(label) {
      if (!label || !this.isSelected) return;
      const match = this._findSizeButton(label);
      if (match) this._select(match);
    }
    _pickSize(btn) {
      if (btn.disabled) return;
      const sameVariant = this.dataset.variantId === btn.dataset.variantId;
      if (sameVariant && this.isSelected) { this._deselect(); return; }
      this._select(btn);
    }
    _select(btn) {
      const wasSelected = this.isSelected;
      this.dataset.variantId = btn.dataset.variantId;
      this.dataset.selected = 'true';
      if (!wasSelected) this.dataset.qty = '1';
      this.querySelectorAll('[data-gs-v4-tee-size]').forEach(b => {
        const active = b === btn;
        b.classList.toggle('is-selected', active);
        b.setAttribute('aria-pressed', String(active));
      });
      this.classList.add('is-selected');
      const label = btn.dataset.sizeLabel || '—';
      const labelEl = this.querySelector('[data-tee-size-label]');
      if (labelEl) labelEl.textContent = label;
      this.classList.remove('is-expanded-sizes');
      this.dataset.expanded = 'false';
      const trigger = this.querySelector('[data-gs-v4-tee-size-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      this._updateQtyUI();
    }
    _deselect() {
      this.dataset.variantId = '';
      this.dataset.selected = 'false';
      this.dataset.qty = '1';
      this.querySelectorAll('[data-gs-v4-tee-size]').forEach(b => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      this.classList.remove('is-selected');
      this.classList.remove('is-expanded-sizes');
      const labelEl = this.querySelector('[data-tee-size-label]');
      if (labelEl) labelEl.textContent = '—';
      this._updateQtyUI();
    }
  }
  if (!customElements.get('gs-v4-tee-card')) {
    customElements.define('gs-v4-tee-card', GsV4TeeCard);
  }

  // ===========================================================================
  // <gs-v4-tee-picker> — exposes getSelectedTees()
  // ===========================================================================
  class GsV4TeePicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this._onClick);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }
    _onClick = (e) => {
      const tab = e.target.closest('[data-gs-v4-tab]');
      if (!tab || !this.contains(tab)) return;
      const idx = tab.dataset.gsV4Tab;
      this.querySelectorAll('[data-gs-v4-tab]').forEach(t => {
        const active = t.dataset.gsV4Tab === idx;
        t.classList.toggle('gs-v3-tabs__tab--active', active);
        t.setAttribute('aria-selected', String(active));
      });
      this.querySelectorAll('[data-gs-v4-panel]').forEach(p => {
        p.classList.toggle('gs-v3-tab-panel--hidden', p.dataset.gsV4Panel !== idx);
      });
    };
    getSelectedTees() {
      return [...this.querySelectorAll('gs-v4-tee-card[data-selected="true"]')]
        .filter(c => c.dataset.variantId)
        .map(c => ({ productId: c.dataset.productId, variantId: c.dataset.variantId, qty: c.qty || 1 }));
    }
  }
  if (!customElements.get('gs-v4-tee-picker')) {
    customElements.define('gs-v4-tee-picker', GsV4TeePicker);
  }

  // ===========================================================================
  // <gs-v4-coordinator>
  // ===========================================================================
  class GsV4Coordinator extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productId = Number(this.dataset.productId);
      this.productFormId = this.dataset.productFormId;
      this.busy = false;

      this.packSel = document.querySelector('[data-gs-v4-pack-selector]');
      this.picker = document.querySelector('[data-gs-v4-picker]');
      this.tees = document.querySelector('[data-gs-v4-tees]');
      if (!this.packSel || !this.picker) {
        console.warn('[gs-v4] missing required blocks; native submit will fire');
        return;
      }

      this._click = (e) => this._onAddClick(e);
      document.addEventListener('click', this._click, true);

      this._onUpdate = () => this._release();
      this._onError = () => this._release();
      document.addEventListener('cart:update', this._onUpdate);
      document.addEventListener('cart:error', this._onError);
    }

    disconnectedCallback() {
      if (this._click) document.removeEventListener('click', this._click, true);
      if (this._onUpdate) document.removeEventListener('cart:update', this._onUpdate);
      if (this._onError) document.removeEventListener('cart:error', this._onError);
    }

    _onAddClick(e) {
      const btn = e.target.closest('[data-to-cart]');
      if (!btn) return;
      if (btn.dataset.cgbPairHandled === 'true') return;

      const form = btn.closest('form[data-product-form-id]');
      if (form && form.dataset.productFormId !== this.productFormId) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      btn.dataset.cgbPairHandled = 'true';

      if (this.busy) return;
      this.busy = true;
      this.activeBtn = btn;
      btn.disabled = true;
      btn.classList.add('!loading');

      this._submit().catch(err => {
        console.error('[gs-v4]', err);
        this._release();
      });
    }

    _release() {
      this.busy = false;
      if (this.activeBtn) {
        this.activeBtn.disabled = false;
        this.activeBtn.classList.remove('!loading');
        delete this.activeBtn.dataset.cgbPairHandled;
        this.activeBtn = null;
      }
    }

    async _submit() {
      const pack = this.packSel.selected;

      if (!this.picker.isComplete()) {
        this.picker.flagIncomplete();
        this._release();
        return;
      }

      const designs = this.picker.getSelectedDesigns();
      const teeSelections = this.tees ? this.tees.getSelectedTees() : [];

      // Additive semantics: each ADD TO CART appends a fresh pack as
      // independent line items. Existing pack lines in the cart (whether from
      // this PDP or another v4 PDP) are NOT wiped — customer can stack
      // multiple packs in one order.
      const mainId = String(this.productId);
      const items = [
        ...designs.map((d, i) => ({
          id: Number(d.variantId),
          quantity: d.qty || 1,
          properties: {
            _gs_v4_row: String(i + 1),
            _gs_v4_pack: String(pack),
            _gs_v4_main: mainId,
          },
        })),
        ...teeSelections.map(t => ({
          id: Number(t.variantId),
          quantity: t.qty || 1,
          properties: {
            _gs_v4_tee: '1',
            _gs_v4_pack: String(pack),
            _gs_v4_main: mainId,
          },
        })),
      ];

      publishEvent('cart:add', { items }, this.sectionId);
    }
  }
  if (!customElements.get('gs-v4-coordinator')) {
    customElements.define('gs-v4-coordinator', GsV4Coordinator);
  }
})();
